import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Type, Hash, ToggleLeft, Users, List } from 'lucide-react';
import Icon from '../../../shared/components/Icon';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import {
  getFixedUserProperties,
  getDefaultOperator,
  getOperatorsForUserType,
  getNumericCountOperators,
} from '../config/userFilterProperties';
import { DEFAULT_WHO_TIME_FILTER } from '../utils/whoTimeFilterUtils';

const ObjectIcon = ({ color = "currentColor", size = 18 }) => (
  <span style={{ fontSize: size, color }} className="font-mono">{"{}"}</span>
);

function getDataTypeIcon(type) {
  switch (type) {
    case 'string': return Type;
    case 'number': return Hash;
    case 'datetime':
    case 'date': return ({ color = "currentColor", size = 18 }) => <Icon name="calendar" size={size} color={color} />;
    case 'boolean': return ToggleLeft;
    case 'list': return List;
    case 'object': return ObjectIcon;
    default: return Type;
  }
}

/**
 * User filter property picker: 4 tabs (All, Users, Events, Transactions).
 * Same UX as SessionPropertyFilterModule.
 * - Users: fixed + custom user identity properties (normal operators by type)
 * - Events: event names as properties, numeric operators for count
 * - Transactions: contract.method as properties, numeric operators for count
 */
const UserPropertyFilterModule = ({
  isOpen,
  onClose,
  triggerRef,
  teamId,
  filters = [],
  existingFilter = null,
  onAdd = () => {},
}) => {
  const [selectedNavItem, setSelectedNavItem] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [userProperties, setUserProperties] = useState(() => getFixedUserProperties());
  const [eventProperties, setEventProperties] = useState([]);
  const [transactionProperties, setTransactionProperties] = useState([]);
  const [loadingUserCustom, setLoadingUserCustom] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [cohorts, setCohorts] = useState([]);
  const [loadingCohorts, setLoadingCohorts] = useState(false);
  const [expandedObjectProperty, setExpandedObjectProperty] = useState(null); // Object selected -> show nested props inline
  const popupRef = useRef(null);

  const getNumericTeamId = () => {
    let id = typeof teamId === 'object' && teamId !== null
      ? parseInt(teamId._id || teamId.id, 10)
      : parseInt(teamId, 10);
    if (typeof teamId === 'string' && isNaN(id)) {
      try {
        const td = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
        id = parseInt(td._id || td.id, 10);
      } catch (_) {}
    }
    return isNaN(id) ? null : id;
  };

  useEffect(() => {
    if (isOpen && triggerRef?.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      setPosition({
        top: rect.bottom + scrollY + 8,
        left: rect.left + scrollX,
      });
    }
  }, [isOpen, triggerRef]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isOpen &&
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, triggerRef]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        if (expandedObjectProperty) setExpandedObjectProperty(null);
        else onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose, expandedObjectProperty]);

  useEffect(() => { if (!isOpen) setExpandedObjectProperty(null); }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !teamId) return;
    const numericTeamId = getNumericTeamId();
    if (!numericTeamId) return;
    setLoadingUserCustom(true);
    axiosInstance.get('/events/user-custom-properties', { params: { team_id: numericTeamId, limit: 5000 } })
      .then((res) => {
        if (res.data?.success && res.data.custom_properties) {
          const custom = res.data.custom_properties.map((prop) => {
            let propertyType = prop.type || 'string';
            let listItemType = null;
            if (propertyType.startsWith('list<') && propertyType.endsWith('>')) {
              listItemType = propertyType.slice(5, -1);
              propertyType = 'list';
            } else if (propertyType === 'list') {
              listItemType = 'string'; // fallback when backend returns bare 'list' (e.g. empty array)
            }
            return {
              name: `custom.${prop.name}`,
              label: prop.name,
              type: propertyType,
              listItemType,
              source: 'user',
              isCustom: true,
              sampleValues: prop.sampleValues || [],
              nestedProperties: prop.nestedProperties || undefined,
            };
          });
          setUserProperties([...getFixedUserProperties(), ...custom]);
        }
      })
      .catch(() => setUserProperties(getFixedUserProperties()))
      .finally(() => setLoadingUserCustom(false));
  }, [isOpen, teamId]);

  useEffect(() => {
    if (!isOpen || !teamId) return;
    const numericTeamId = getNumericTeamId();
    if (!numericTeamId) return;
    setLoadingEvents(true);
    axiosInstance.get('/events/summaries', { params: { team_id: numericTeamId } })
      .then((res) => {
        const summaries = res.data?.summaries || [];
        const names = [...new Set(summaries.map((s) => s.event_name).filter(Boolean))].sort();
        setEventProperties(names.map((n) => ({
          name: `event_count:${n}`,
          label: n,
          type: 'event_count',
          source: 'event',
        })));
      })
      .catch(() => setEventProperties([]))
      .finally(() => setLoadingEvents(false));
  }, [isOpen, teamId]);

  useEffect(() => {
    if (!isOpen || !teamId) return;
    const numericTeamId = getNumericTeamId();
    if (!numericTeamId) return;
    setLoadingTransactions(true);
    axiosInstance.get(`/transactions/team/${encodeURIComponent(numericTeamId)}/contract-methods`)
      .then((res) => {
        const methods = res.data?.contractMethods || [];
        setTransactionProperties(methods.map((m) => {
          const key = `${m.contractName}.${m.functionName}`;
          return {
            name: `transaction_count:${key}`,
            label: key,
            type: 'transaction_count',
            source: 'transaction',
          };
        }));
      })
      .catch(() => setTransactionProperties([]))
      .finally(() => setLoadingTransactions(false));
  }, [isOpen, teamId]);

  useEffect(() => {
    if (!isOpen || selectedNavItem !== 'cohorts') return;
    const numericTeamId = getNumericTeamId();
    if (!numericTeamId) return;
    setLoadingCohorts(true);
    axiosInstance.get('/cohorts', { params: { team_id: numericTeamId, include_user_count: false } })
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.cohorts)) {
          setCohorts(res.data.cohorts);
        }
      })
      .catch(() => setCohorts([]))
      .finally(() => setLoadingCohorts(false));
  }, [isOpen, selectedNavItem, teamId]);

  const handleCohortClick = (cohort) => {
    onAdd({
      id: existingFilter?.id || `user-filter-cohort-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      category: 'cohort',
      propertySource: 'cohort',
      field: 'cohort',
      value: [cohort.id],
      propertyLabel: cohort.name,
      connector: existingFilter?.connector ?? 'AND',
    });
    onClose();
  };

  if (!isOpen) return null;

  const allProperties = [...userProperties, ...eventProperties, ...transactionProperties];

  const getPropsForTab = () => {
    switch (selectedNavItem) {
      case 'users': return userProperties;
      case 'events': return eventProperties;
      case 'transactions': return transactionProperties;
      case 'all': return allProperties;
      default: return allProperties;
    }
  };

  const propsList = getPropsForTab();
  const filteredProps = propsList.filter(
    (p) =>
      (p.label || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategory = (prop) => {
    if (prop.source === 'user') return 'user';
    if (prop.source === 'event' || prop.type === 'event_count') return 'event';
    if (prop.source === 'transaction' || prop.type === 'transaction_count') return 'transaction';
    return 'user';
  };

  const handleSelectNestedProperty = (parentProperty, nestedProp) => {
    const category = getCategory(parentProperty);
    const nestedType = nestedProp.type || 'string';
    let listItemType = null;
    if (nestedType.startsWith('list<') && nestedType.endsWith('>')) {
      listItemType = nestedType.slice(5, -1);
    }
    const fullPath = `${parentProperty.name}.${nestedProp.name}`;
    const effectiveType = nestedType.startsWith('list') ? 'list' : nestedType;
    const newFilter = {
      id: existingFilter?.id || `user-filter-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      category,
      propertySource: category,
      connector: existingFilter?.connector,
      field: fullPath,
      property: fullPath,
      propertyLabel: `${parentProperty.label}.${nestedProp.name}`,
      propertyType: effectiveType,
      listItemType: listItemType || undefined,
      operator: getDefaultOperator(effectiveType),
      value: null,
      value2: null,
      sampleValues: nestedProp.sampleValues || [],
      nestedPropertyKey: nestedProp.name,
      listMode: effectiveType === 'list' ? 'any_in_list' : undefined,
    };
    onAdd(newFilter);
    setExpandedObjectProperty(null);
    onClose();
  };

  const handleSelectProperty = (property) => {
    if (property.type === 'object' && property.nestedProperties?.length > 0) {
      setExpandedObjectProperty(property);
      return;
    }
    const category = getCategory(property);
    const isCountType = property.type === 'event_count' || property.type === 'transaction_count';
    const effectiveType = isCountType ? 'number' : property.type;
    const operator = isCountType ? 'equal' : getDefaultOperator(property.type);

    const newFilter = {
      id: existingFilter?.id || `user-filter-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      category,
      propertySource: category,
      connector: existingFilter?.connector,
      field: property.name,
      property: property.name,
      propertyLabel: property.label,
      propertyType: effectiveType,
      operator: existingFilter && existingFilter.propertyType === effectiveType ? existingFilter.operator : operator,
      value: existingFilter && existingFilter.propertyType === effectiveType ? existingFilter.value : null,
      value2: existingFilter?.value2 ?? null,
      ...(property.sampleValues && { sampleValues: property.sampleValues }),
      ...(property.listItemType != null && { listItemType: property.listItemType }),
      ...(property.nestedProperties && { nestedProperties: property.nestedProperties }),
      ...(property.type === 'list' && { listMode: existingFilter?.listMode || 'any_in_list' }),
      ...(isCountType && {
        didOperator: existingFilter?.didOperator ?? 'did',
        eventTimeFilter: existingFilter?.eventTimeFilter ?? { ...DEFAULT_WHO_TIME_FILTER },
      }),
    };
    onAdd(newFilter);
    onClose();
  };

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'users', label: 'Users' },
    { id: 'events', label: 'Events' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'cohorts', label: 'Cohorts' },
  ];

  const isLoading = (loadingUserCustom && selectedNavItem !== 'events' && selectedNavItem !== 'transactions' && selectedNavItem !== 'cohorts') ||
    (loadingEvents && (selectedNavItem === 'events' || selectedNavItem === 'all')) ||
    (loadingTransactions && (selectedNavItem === 'transactions' || selectedNavItem === 'all')) ||
    (loadingCohorts && selectedNavItem === 'cohorts');

  return createPortal(
    <div
      ref={popupRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl flex flex-col overflow-hidden"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: '400px',
        maxHeight: '360px',
        fontFamily: 'Raleway, sans-serif',
      }}
    >
      <div className="px-3 py-2 border-b border-gray-200 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#475569]" />
          <input
            type="text"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#475569]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="w-24 border-r border-gray-200 bg-gray-50 flex-shrink-0 py-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setSelectedNavItem(tab.id)}
              className={`w-full text-left px-2 py-1.5 text-xs transition-colors ${
                selectedNavItem === tab.id
                  ? 'bg-white border-l-2 border-[#4b57aa] text-[#4b57aa] font-semibold'
                  : 'text-[#475569] hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-2 flex flex-col">
          {expandedObjectProperty ? (
            <>
              <button
                type="button"
                onClick={() => setExpandedObjectProperty(null)}
                className="flex items-center gap-1 text-xs text-[#4b57aa] hover:underline mb-2"
              >
                ← Back to properties
              </button>
              <div className="text-xs font-medium text-[#475569] mb-2">
                Select nested property in "{expandedObjectProperty.label}"
              </div>
              <div className="space-y-1">
                {expandedObjectProperty.nestedProperties?.map((np) => {
                  const NestedIcon = getDataTypeIcon(np.type?.startsWith('list') ? 'list' : np.type || 'string');
                  return (
                    <button
                      key={np.name}
                      type="button"
                      onClick={() => handleSelectNestedProperty(expandedObjectProperty, np)}
                      className="w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-50 flex items-center gap-2"
                    >
                      <NestedIcon className="h-4 w-4 text-[#475569] flex-shrink-0" />
                      <span className="flex-1 text-[#0f172a]">{np.name}</span>
                      <span className="text-xs text-[#64748b]">{np.type || 'string'}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : selectedNavItem === 'cohorts' ? (
            <>
              {loadingCohorts ? (
                <div className="p-4 text-sm text-[#475569] text-center">Loading cohorts…</div>
              ) : cohorts.length === 0 ? (
                <div className="p-4 text-sm text-[#475569] text-center">No cohorts. Create cohorts in the Cohorts page.</div>
              ) : (
                <div className="flex-1 overflow-y-auto space-y-0.5">
                  {cohorts
                    .filter((c) => !searchTerm || (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((cohort) => (
                      <button
                        key={cohort.id}
                        type="button"
                        onClick={() => handleCohortClick(cohort)}
                        className="w-full text-left px-3 py-2 text-sm rounded transition-colors flex items-center gap-2 hover:bg-gray-50"
                      >
                        <Users className="h-4 w-4 text-[#475569] flex-shrink-0" />
                        <span className="flex-1">{cohort.name}</span>
                      </button>
                    ))}
                </div>
              )}
            </>
          ) : isLoading ? (
            <div className="p-4 text-sm text-[#475569] text-center">Loading...</div>
          ) : filteredProps.length === 0 ? (
            <div className="p-4 text-sm text-[#475569] text-center">
              {searchTerm ? 'No properties match.' : 'No properties.'}
            </div>
          ) : (
            <div className="space-y-0.5">
              {filteredProps.map((prop, idx) => {
                const Icon = getDataTypeIcon(prop.type === 'event_count' || prop.type === 'transaction_count' ? 'number' : prop.type);
                const isSelected = existingFilter && (existingFilter.field === prop.name || existingFilter.property === prop.name);
                const badge = prop.source === 'user' ? 'U' : prop.source === 'event' ? 'E' : 'T';
                return (
                  <button
                    key={`${prop.source}-${prop.name}-${idx}`}
                    type="button"
                    onClick={() => handleSelectProperty(prop)}
                    className={`w-full text-left px-3 py-2 text-sm rounded transition-colors flex items-center gap-2 ${
                      isSelected ? 'bg-gray-200 border border-gray-300' : 'hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-4 w-4 text-[#475569] flex-shrink-0" />
                    <span className="flex-1">{prop.label}</span>
                    <span
                      className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-gray-200 text-[#475569]"
                      title={prop.source}
                    >
                      {badge}
                    </span>
                    {isSelected && <span className="text-xs text-[#475569]">(current)</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UserPropertyFilterModule;
export { getDataTypeIcon };
